// popover-support.js
// Single public API: position(menuElm, fromRect, opts = {})
//   • menuElm   – DOM element that contains `.popover-menu` and `.arrow-border`
//   • fromRect  – DOMRect‑like {top,left,width,height} of the target button
//   • opts      – { prefer: 'dn'|'up'|'rt'|'lf', outside: bool }
// Returns the direction picked (or null if nothing fit).
// The function mutates `menuElm.style.left/top` and arrow CSS vars.

/*──────────────────────── constants ────────────────────────*/
const padding     = 16;   // free space from viewport edge
const arrowW      = 22;   // full width of arrow incl. border
const arrowH      = 12;   // full height of arrow incl. border
const sideGutter  = 10;   // .lf/.rt popover-menu side margin reserved for arrow
const edgeAnchorInset = sideGutter - arrowH / 2; // matches popover.scss margins and arrow placement
const directions  = ['dn', 'rt', 'lf', 'up'];
const sideArrowInset = (arrowW - arrowH) / 2;

/*──────────────────────── internal helpers ─────────────────*/
function clamp(v, lo, hi) {
  if (hi < lo) hi = lo;
  return Math.min(Math.max(v, lo), hi);
}

function vp() {
  const vv = window.visualViewport;
  if (vv) {
    return {
      left: window.scrollX + vv.offsetLeft,
      top: window.scrollY + vv.offsetTop,
      width: vv.width,
      height: vv.height
    };
  }

  return {
    left: window.scrollX,
    top: window.scrollY,
    width: window.innerWidth,
    height: window.innerHeight
  };
}

function setArrowCSS(elm, x, y) {
  elm.style.setProperty('--ax', `${x}px`);
  elm.style.setProperty('--ay', `${y}px`);
}

function setDirectionClass(elm, dir) {
  directions.forEach(d => elm.classList.remove(d));
  elm.classList.add(dir);
}

function xForUpDown(ctx) {
  const ideal = ctx.targetCenterX - ctx.popW / 2;
  return clamp(ideal, padding, ctx.vp.width - ctx.popW - padding);
}

function yForLeftRight(ctx) {
  const ideal = ctx.targetCenterY - ctx.popH / 2;
  return clamp(ideal, padding, ctx.vp.height - ctx.popH - padding);
}

function arrowXForTarget(ctx, menuX) {
  return clamp(
    ctx.targetCenterX - menuX - arrowW / 2,
    0,
    Math.max(0, ctx.popW - arrowW)
  );
}

function arrowYForTarget(ctx, menuY) {
  return clamp(
    ctx.targetCenterY - menuY - arrowW / 2,
    0,
    Math.max(0, ctx.popH - arrowW)
  );
}

function arrowXForLeftSide() {
  return sideGutter - arrowW - sideArrowInset;
}

function arrowXForRightSide(ctx) {
  return ctx.popW - sideGutter - sideArrowInset;
}

function overflowScore(x, y, w, h, viewport) {
  const leftOverflow = Math.max(0, padding - x);
  const topOverflow = Math.max(0, padding - y);
  const rightOverflow = Math.max(0, (x + w + padding) - viewport.width);
  const bottomOverflow = Math.max(0, (y + h + padding) - viewport.height);

  return {
    total: leftOverflow + topOverflow + rightOverflow + bottomOverflow,
    leftOverflow,
    topOverflow,
    rightOverflow,
    bottomOverflow,
  };
}

function clampPosition(x, y, w, h, viewport) {
  return [
    clamp(x, padding, viewport.width - w - padding),
    clamp(y, padding, viewport.height - h - padding),
  ];
}

/*──────────────────── directional strategies ───────────────*/
const strategies = {
  dn(menu, c) {
    const gap = c.outside ? c.targetH : 0; // from edge of target rect
    return {
      x: c.targetCenterX - c.popW / 2,
      y: c.targetBottom + gap - edgeAnchorInset,
      applyArrow(menuElm, x, y) {
        setArrowCSS(menuElm, arrowXForTarget(c, x), - (arrowW / 2) - 2);  // arrowW because arrow is rotated
      }
    };
  },
  up(menu, c) {
    const gap = c.outside ? c.targetH : 0;
    return {
      x: c.targetCenterX - c.popW / 2,
      y: c.targetTop - c.popH - gap + edgeAnchorInset,
      applyArrow(menuElm, x, y) {
        setArrowCSS(menuElm, arrowXForTarget(c, x), c.popH - arrowW / 2); // arrowW because arrow is rotated
      }
    };
  },
  rt(menu, c) {
    const gap = c.outside ? c.targetW : 0;
    return {
      x: c.targetRight + gap - edgeAnchorInset,
      y: c.targetCenterY - c.popH / 2,
      applyArrow(menuElm, x, y) {
        setArrowCSS(menuElm, arrowXForLeftSide(), arrowYForTarget(c, y));
      }
    };
  },
  lf(menu, c) {
    const gap = c.outside ? c.targetW : 0;
    return {
      x: c.targetLeft - c.popW - gap + edgeAnchorInset,
      y: c.targetCenterY - c.popH / 2,
      applyArrow(menuElm, x, y) {
        setArrowCSS(menuElm, arrowXForRightSide(c), arrowYForTarget(c, y));
      }
    };
  },
};

/*────────────────────────── core export ─────────────────────*/
export function position(menuElm, fromRect, opts = {}) {
  if (!menuElm || !menuElm.querySelector) throw new Error('menuElm missing');
  if (!menuElm.querySelector('.arrow-border')) throw new Error('arrow element .arrow-border missing');

  const { prefer = 'dn', outside = false } = opts;

  const c = {
    targetLeft: fromRect.left,
    targetRight: fromRect.left + fromRect.width,
    targetTop: fromRect.top,
    targetBottom: fromRect.top + fromRect.height,
    targetCenterX: fromRect.left + fromRect.width  / 2,
    targetCenterY: fromRect.top  + fromRect.height / 2,
    targetW: fromRect.width,
    targetH: fromRect.height,
    vp: vp(),
    outside,
  };
  // order directions with preference first
  const dirs = [...directions];
  const idx = dirs.indexOf(prefer);
  if (idx > 0) dirs.unshift(dirs.splice(idx, 1)[0]);

  let bestDir = null;
  let bestPlan = null;
  let bestContext = null;
  let bestOverflow = null;

  for (const dir of dirs) {
    setDirectionClass(menuElm, dir);

    // Measure with the candidate direction class applied. The direction class
    // changes the popover-menu margin, so measuring once under the previous
    // class can misplace side-opening arrows.
    const { width: popW, height: popH } = menuElm.getBoundingClientRect();
    const candidateContext = { ...c, popW, popH };
    const plan = strategies[dir](menuElm, candidateContext);
    const overflow = overflowScore(plan.x, plan.y, candidateContext.popW, candidateContext.popH, candidateContext.vp);

    if (overflow.total === 0) {
      bestDir = dir;
      bestPlan = plan;
      bestContext = candidateContext;
      bestOverflow = overflow;
      break;
    }

    if (!bestOverflow || overflow.total < bestOverflow.total) {
      bestDir = dir;
      bestPlan = plan;
      bestContext = candidateContext;
      bestOverflow = overflow;
    }
  }

  if (!bestPlan) return null;

  setDirectionClass(menuElm, bestDir);

  // Clamp the chosen direction into the viewport so oversized popovers stay visible.
  const [x, y] = clampPosition(bestPlan.x, bestPlan.y, bestContext.popW, bestContext.popH, bestContext.vp);
  bestPlan.applyArrow(menuElm, x, y);
  menuElm.style.left = `${x + bestContext.vp.left}px`;
  menuElm.style.top  = `${y + bestContext.vp.top }px`;

  return bestDir;
}

export default { position };
